import { faFlag, faStar } from '@fortawesome/free-solid-svg-icons';

/**
 * Структуровані факти історії СГОСТУ — ADR-0011.
 *
 * На відміну від вільного тексту (`history.text` у CMS), ці масиви жорстко
 * прив'язані до коду: дати, назви турнірів, договорів і партнерів — реальні
 * факти організації, а не контент, який редактор має редагувати з адмінки.
 * Тримати їх у CMS-полі одним рядком тексту довелося б у вигляді нечитабельної
 * стіни — таблиці й хронологія тут виразніші за суцільний абзац.
 *
 * Кожен рядок — те, що прямо повідомила організація; нічого не додумано й
 * не округлено сильніше, ніж у першоджерелі (лишені "біля", "кілька десятків"
 * там, де точного числа немає).
 */

export const STATS = [
  {
    value: '1999',
    ua: 'рік заснування',
    en: 'year founded',
    hintUa: '18 січня — установча конференція, 3 березня — реєстрація Міністерством юстиції',
    hintEn: '18 January — founding conference, registered by the Ministry of Justice on 3 March',
  },
  {
    value: '~1500',
    ua: 'змагань проведено',
    en: 'competitions held',
    hintUa: 'за роки роботи, у більшості областей України',
    hintEn: 'over the years, across most of Ukraine’s regions',
  },
  {
    value: 'Майже вся Україна',
    ua: 'територіальних підрозділів',
    en: 'territorial subdivisions',
    hintUa: 'у майже всіх областях країни',
    hintEn: 'in nearly every region of the country',
  },
  {
    value: '~1000',
    ua: 'кандидатів і членів',
    en: 'candidates and members',
    hintUa: 'та кілька десятків танцювальних клубів',
    hintEn: 'and several dozen dance clubs',
  },
];

export const TOURNAMENTS = [
  {
    key: 'championship',
    nameUa: '«Чемпіонат СГОСТУ»',
    nameEn: '"SGOSTU Championship"',
    years: '1999–2019',
    noteUa: 'щорічні всеукраїнські змагання — ровесник самої організації',
    noteEn: 'annual national championship — as old as the organisation itself',
  },
  {
    key: 'kyiv-waltz',
    nameUa: '«Київський вальс»',
    nameEn: '"Kyiv Waltz"',
    years: '2002–2018',
    noteUa: 'щорічні міжнародні змагання',
    noteEn: 'annual international tournament',
  },
  {
    key: 'inspiration-cup',
    nameUa: '«Відкриття сезону» → «Inspiration Cup»',
    nameEn: '"Season Opening" → "Inspiration Cup"',
    years: '2005–2019',
    noteUa: 'старт сезону, що отримав нове ім’я після ребрендингу',
    noteEn: 'the season-opener, renamed after a rebrand',
  },
  {
    key: 'pearls',
    nameUa: '«Перлини сезону»',
    nameEn: '"Pearls of the Season"',
    years: '2007–2021',
    noteUa: 'міжрегіональні рейтингові змагання',
    noteEn: 'interregional ranking tournament',
  },
  {
    key: 'marathon',
    nameUa: '«Танцювальний марафон»',
    nameEn: '"Dance Marathon"',
    years: '2011–2021',
    noteUa: 'міжрегіональні рейтингові змагання',
    noteEn: 'interregional ranking tournament',
  },
  {
    key: 'dsl',
    nameUa: '«Dance Sport Life»',
    nameEn: '"Dance Sport Life"',
    years: '2012–2021',
    noteUa: 'щорічні всеукраїнські змагання',
    noteEn: 'annual national tournament',
  },
];

// Хронологічно за роком першої угоди. Абревіатура — офіційна назва
// організації-партнера; де першоджерело не розкриває абревіатуру (ЦСЛТ,
// ВТЛ), повної назви тут немає навмисно — вигадувати її нема підстав.
export const PARTNERS = [
  {
    key: 'pkst',
    year: '1999',
    short: 'ПКСТ',
    shortEn: 'PKST',
    nameUa: 'Професійна Київська Спілка тренерів',
    nameEn: 'Professional Kyiv Coaches Union',
    personUa: 'Президент Марченко Сергій',
    personEn: 'President Serhii Marchenko',
  },
  {
    key: 'urt',
    year: '2000, 2004',
    short: 'УРТ',
    shortEn: 'URT',
    nameUa: 'Українська Рада танцю',
    nameEn: 'Ukrainian Dance Council',
    personUa: 'Президент Ірена Бусь, Львів',
    personEn: 'President Irena Bus, Lviv',
  },
  {
    key: 'ufst',
    year: '2001',
    short: 'УФСТ',
    shortEn: 'UFST',
    nameUa: 'Українська федерація спортивного танцю',
    nameEn: 'Ukrainian Federation of DanceSport',
    personUa: 'Президент Пінчук Сергій, Харків',
    personEn: 'President Serhii Pinchuk, Kharkiv',
  },
  {
    key: 'tutti',
    year: '2002',
    short: 'ЦСЛТ «Тутті»',
    shortEn: 'TsSLT "Tutti"',
    nameUa: '',
    nameEn: '',
    personUa: 'Директор Климчук Лариса, Харків',
    personEn: 'Director Larysa Klymchuk, Kharkiv',
  },
  {
    key: 'idsf',
    year: '2005',
    short: 'IDSF',
    shortEn: 'IDSF',
    nameUa: 'International DanceSport Federation — у складі Союзу організацій України',
    nameEn: 'International DanceSport Federation — as part of the Union of Organisations of Ukraine',
    personUa: 'Генеральний секретар Карлос Фрейтаг',
    personEn: 'Secretary General Carlos Freitag',
  },
  {
    key: 'astu',
    year: '2006, 2008',
    short: 'АСТУ',
    shortEn: 'ASTU',
    nameUa: 'Асоціація Спортивного танцю України',
    nameEn: 'Sports Dance Association of Ukraine',
    personUa: 'Президент Святослав Влох',
    personEn: 'President Sviatoslav Vlokh',
  },
  {
    key: 'usbt',
    year: '2008',
    short: 'УСБТ',
    shortEn: 'USBT',
    nameUa: 'Українська Спілка бального танцю',
    nameEn: 'Ukrainian Ballroom Dance Union',
    personUa: 'Президент Літвінов Олексій, Харків',
    personEn: 'President Oleksii Litvinov, Kharkiv',
  },
  {
    key: 'asetu',
    year: '2008',
    short: 'АСЕТУ',
    shortEn: 'ASETU',
    nameUa: 'Асоціація сучасного та естрадного танцю України',
    nameEn: 'Association of Modern and Show Dance of Ukraine',
    personUa: 'Президент Гладиш Олександр, Сімферополь',
    personEn: 'President Oleksandr Hladysh, Simferopol',
  },
  {
    key: 'ltu',
    year: '2008',
    short: 'ЛТУ',
    shortEn: 'LTU',
    nameUa: 'Ліга танцю України',
    nameEn: 'Dance League of Ukraine',
    personUa: 'Президент Пігулевський Владислав, Київ',
    personEn: 'President Vladyslav Pihulevskyi, Kyiv',
  },
  {
    key: 'wdc',
    year: '2014',
    short: 'WDC',
    shortEn: 'WDC',
    nameUa: 'World Dance Council',
    nameEn: 'World Dance Council',
    personUa: 'Президент Донні Бернс МВЕ',
    personEn: 'President Donnie Burns MBE',
  },
  {
    key: 'vtl',
    year: '2016',
    short: 'ВТЛ',
    shortEn: 'VTL',
    nameUa: '',
    nameEn: '',
    personUa: 'Президент Міркін Роман, Київ',
    personEn: 'President Roman Mirkin, Kyiv',
  },
];

export const FOUNDERS = [
  {
    key: 'idu',
    type: 'founder',
    icon: faFlag,
    year: '2004',
    textUa: 'Засновник Міжнародної Спілки громадських організацій «Міжнародна танцювальна Спілка» (IDU)',
    textEn: 'Founder of the International Union of Public Organisations "International Dance Union" (IDU)',
  },
  {
    key: 'vsotvs',
    type: 'founder',
    icon: faFlag,
    year: '2017',
    textUa: 'Засновник Громадської Спілки «Всеукраїнська Спілка організацій танцювальних видів спорту»',
    textEn: 'Founder of the Public Union "All-Ukrainian Union of Dance Sport Organisations"',
  },
  {
    key: 'vbu',
    type: 'founder',
    icon: faFlag,
    year: '2022',
    textUa: 'Співзасновник об’єднання «Все буде Україна» — разом з ВРСТ, УАВТ, УРСТ та УФСТ',
    textEn: 'Co-founder of the "All of Ukraine Will Prevail" alliance — with VRST, UAVT, URST and UFST',
  },
  {
    key: 'wdcal',
    type: 'member',
    icon: faStar,
    year: '2010–2019',
    textUa: 'Член World Dance Council Amateur League (WDC AL)',
    textEn: 'Member of the World Dance Council Amateur League (WDC AL)',
  },
  {
    key: 'eadc',
    type: 'member',
    icon: faStar,
    year: '2018–2022',
    textUa: 'Член Euro Asian Dance Council (EADC)',
    textEn: 'Member of the Euro Asian Dance Council (EADC)',
  },
];
